import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';
import { ComprehensiveStockAnalysis } from '@/types/perplexity.types';
import { SkeletonCard } from './SkeletonCard';

interface FundamentalAnalysisCardProps {
  data: ComprehensiveStockAnalysis['fundamentalAnalysis'] | null | undefined;
  isLoading: boolean;
}

export const FundamentalAnalysisCard: React.FC<FundamentalAnalysisCardProps> = ({
  data,
  isLoading,
}) => {
  if (isLoading || !data) {
    return <SkeletonCard lines={6} />;
  }

  const metrics = [
    { label: 'Revenue', value: data.keyMetrics.revenue, icon: 'trending-up' },
    { label: 'EPS', value: data.keyMetrics.eps, icon: 'cash' },
    { label: 'P/E Ratio', value: data.keyMetrics.pe, icon: 'calculator' },
    { label: 'Debt/Equity', value: data.keyMetrics.debtToEquity, icon: 'scale' },
    { label: 'ROE', value: data.keyMetrics.roe, icon: 'pie-chart' },
    { label: 'Free Cash Flow', value: data.keyMetrics.fcf, icon: 'water' },
  ];

  return (
    <View style={styles.card}>
      <View style={styles.cardHeader}>
        <Ionicons name="calculator" size={20} color={Colors.primary} />
        <Text style={styles.cardTitle}>Fundamental Analysis</Text>
      </View>

      {/* Key Metrics Grid */}
      <View style={styles.metricsGrid}>
        {metrics.map((metric, index) => (
          <View key={index} style={styles.metricBox}>
            <View style={styles.metricHeader}>
              <Ionicons name={metric.icon as any} size={14} color={Colors.text.secondary} />
              <Text style={styles.metricLabel}>{metric.label}</Text>
            </View>
            <Text style={styles.metricValue}>{metric.value}</Text>
          </View>
        ))}
      </View>

      <View style={styles.divider} />

      {/* Valuation Assessment */}
      <View style={styles.section}>
        <Text style={styles.sectionLabel}>Valuation</Text>
        <Text style={styles.cardText}>{data.valuation}</Text>
      </View>

      {/* Peer Comparison */}
      <View style={styles.section}>
        <Text style={styles.sectionLabel}>Peer Comparison</Text>
        <Text style={styles.cardText}>{data.peerComparison}</Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: Colors.background.secondary,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: Colors.text.primary,
    flex: 1,
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  metricBox: {
    flex: 1,
    minWidth: '47%',
    backgroundColor: Colors.background.primary,
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
  },
  metricHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginBottom: 4,
  },
  metricLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: Colors.text.secondary,
  },
  metricValue: {
    fontSize: 13,
    lineHeight: 18,
    color: Colors.text.primary,
    fontWeight: '500',
  },
  divider: {
    height: 1,
    backgroundColor: Colors.border.subtle,
    marginVertical: 12,
  },
  section: {
    marginBottom: 12,
  },
  sectionLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: Colors.text.secondary,
    marginBottom: 6,
  },
  cardText: {
    fontSize: 14,
    lineHeight: 20,
    color: Colors.text.primary,
  },
});
